import React from 'react';

const shimmerDelay = (ms) => ({'--shimmer-delay': `${ms}ms`});

function OverviewCardSkeleton({delay = 0}) {
    return (
        <div className="col-sm-6 col-md-4 col-xl-2 skeleton-item" style={{animationDelay: `${delay}ms`}}>
            <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-3">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <span
                            className="skeleton-block skeleton-circle"
                            style={{width: 48, height: 48, ...shimmerDelay(delay + 40)}}
                        />
                        <span
                            className="skeleton-block"
                            style={{height: 20, width: 20, borderRadius: '50%', ...shimmerDelay(delay + 60)}}
                        />
                    </div>
                    <span
                        className="skeleton-block mb-2 d-block"
                        style={{height: 12, width: '55%', ...shimmerDelay(delay + 80)}}
                    />
                    <span
                        className="skeleton-block mb-2 d-block"
                        style={{height: 28, width: '45%', ...shimmerDelay(delay + 100)}}
                    />
                    <span
                        className="skeleton-block d-block"
                        style={{height: 12, width: '65%', ...shimmerDelay(delay + 120)}}
                    />
                </div>
            </div>
        </div>
    );
}

function ChartBarsSkeleton({delay = 0, barCount = 12, barHeights}) {
    const heights = barHeights || Array.from(
        {length: barCount},
        (_, i) => 80 + ((i * 23) % 140)
    );

    return (
        <div
            className="dashboard-chart-bars d-flex align-items-end justify-content-between gap-1"
            style={{height: 240, paddingTop: '0.5rem'}}
        >
            {heights.map((height, index) => (
                <span
                    key={index}
                    className="skeleton-block"
                    style={{
                        width: '100%',
                        height,
                        ...shimmerDelay(delay + index * 35),
                    }}
                />
            ))}
        </div>
    );
}

function ChartAreaSkeleton({delay = 0, barCount = 12, barHeights}) {
    return (
        <div className="dashboard-chart-skeleton">
            <div className="d-flex justify-content-end gap-2 mb-3">
                <span className="skeleton-block" style={{height: 12, width: 56, ...shimmerDelay(delay + 10)}}/>
                <span className="skeleton-block" style={{height: 12, width: 72, ...shimmerDelay(delay + 20)}}/>
            </div>

            <div className="d-flex">
                <div className="d-flex flex-column justify-content-between me-2 py-1" style={{width: 28, height: 240}}>
                    {[1, 2, 3, 4, 5].map((i) => (
                        <span
                            key={i}
                            className="skeleton-block"
                            style={{height: 8, width: 20, ...shimmerDelay(delay + i * 15)}}
                        />
                    ))}
                </div>

                <div className="d-flex flex-column flex-grow-1 min-w-0">
                    <ChartBarsSkeleton delay={delay + 50} barCount={barCount} barHeights={barHeights}/>
                    <span
                        className="skeleton-block mt-2 d-block"
                        style={{height: 2, width: '100%', ...shimmerDelay(delay + 30)}}
                    />
                    <div className="d-flex justify-content-between mt-2 gap-1">
                        {Array.from({length: Math.min(barCount, 8)}).map((_, i) => (
                            <span
                                key={i}
                                className="skeleton-block"
                                style={{height: 8, width: '100%', maxWidth: 36, ...shimmerDelay(delay + 40 + i * 20)}}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function ChartCardSkeleton({
    titleWidth = '40%',
    delay = 0,
    barCount = 12,
    dropdownCount = 1,
    barHeights,
}) {
    return (
        <div className="card shadow-sm border-0 h-100 skeleton-item" style={{animationDelay: `${delay}ms`}}>
            <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                <span
                    className="skeleton-block"
                    style={{height: 18, width: titleWidth, ...shimmerDelay(delay + 20)}}
                />
                <div className="d-flex gap-2">
                    {Array.from({length: dropdownCount}).map((_, i) => (
                        <span
                            key={i}
                            className="skeleton-block"
                            style={{height: 32, width: i === 0 ? 120 : 100, ...shimmerDelay(delay + 40 + i * 20)}}
                        />
                    ))}
                </div>
            </div>

            <div className="card-body p-3">
                <ChartAreaSkeleton
                    delay={delay + 60}
                    barCount={barCount}
                    barHeights={barHeights}
                />
            </div>

            <div className="card-footer bg-light border-top-0 py-2 px-3">
                <span
                    className="skeleton-block d-block"
                    style={{height: 12, width: '70%', ...shimmerDelay(delay + 200)}}
                />
            </div>
        </div>
    );
}

function SmsDashboardSkeleton() {
    const monthlyBarHeights = [100, 165, 130, 200, 115, 155];

    return (
        <div className="sms-skeleton container-fluid px-4 py-3">
            <div className="row g-3">
                {Array.from({length: 7}).map((_, index) => (
                    <OverviewCardSkeleton key={index} delay={index * 50} />
                ))}
            </div>

            <div className="row mt-4 g-4">
                <div className="col-lg-8">
                    <ChartCardSkeleton
                        titleWidth="38%"
                        delay={350}
                        barCount={14}
                        dropdownCount={2}
                    />
                </div>
                <div className="col-lg-4">
                    <ChartCardSkeleton
                        titleWidth="48%"
                        delay={400}
                        barCount={6}
                        dropdownCount={1}
                        barHeights={monthlyBarHeights}
                    />
                </div>
            </div>
        </div>
    );
}

export default SmsDashboardSkeleton;
